/**
 * Resume load-normalization + YAML export projection.
 *
 * Extracted out of ResumeEditor.tsx so both can be unit-tested, and so neither
 * re-types a resume item's field list: every field list here comes from
 * `./resumeSchema`, the browser mirror of shared/resume_template.py.
 *
 * The bug that motivated the extraction: `normalizeResumeJson` rebuilt every
 * experience bullet as `{text, tags}` on load, silently discarding `include`.
 * A user who unchecked a bullet, saved, and reopened the editor saw it CHECKED
 * again — and the next save wrote that back, so the exclusion was permanently
 * lost. The YAML export had the same hand-typed-field-list disease. See
 * docs/resume_schema_consolidation_plan.md.
 */
import type { Demographics, ResumeJSON } from '../types';
import { ensureSchemaLists, exportSchemaFields, pickSchemaFields } from './resumeSchema';

export const EMPTY_DEMOGRAPHICS: Demographics = {
  location_country: 'USA',
  authorized_to_work: '',
  require_visa_sponsorship: '',
  gender: '',
  gender_other: '',
  race: '',
  race_other: '',
  veteran: '',
  veteran_other: '',
  disabled: '',
  disabled_other: '',
  salary_floor: '',
  salary_currency: 'USD',
};

/**
 * Currency choices for the Demographics "Salary Expectation Floor" field —
 * the ten most-traded global currencies, US dollar first (and the default).
 * Codes are ISO 4217 so `Intl.NumberFormat` can format with them directly.
 */
export const CURRENCY_OPTIONS: { code: string; label: string }[] = [
  { code: 'USD', label: '$ USD — US Dollar' },
  { code: 'EUR', label: '€ EUR — Euro' },
  { code: 'GBP', label: '£ GBP — British Pound' },
  { code: 'JPY', label: '¥ JPY — Japanese Yen' },
  { code: 'CNY', label: '¥ CNY — Chinese Yuan' },
  { code: 'INR', label: '₹ INR — Indian Rupee' },
  { code: 'CAD', label: 'C$ CAD — Canadian Dollar' },
  { code: 'AUD', label: 'A$ AUD — Australian Dollar' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'HKD', label: 'HK$ HKD — Hong Kong Dollar' },
];

/**
 * Display-format a digits-only salary floor ('225000' -> '$225,000').
 *
 * Storage stays numeric-only (the extension autofills application forms that
 * almost always reject symbols/commas); this is purely what the editor shows
 * when the field is not focused. Never throws — an unknown currency code falls
 * back to plain thousands separators.
 */
export function formatSalaryFloor(digits: string, currency: string): string {
  const clean = String(digits ?? '').replace(/[^0-9]/g, '');
  if (!clean) return '';
  const amount = Number(clean);
  if (!Number.isFinite(amount)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount);
  }
}

// Flat sections normalized in place (schema path === section key).
const FLAT_SECTIONS = [
  'skills',
  'education',
  'awards',
  'presentations',
  'publications',
  'portfolios',
  'summary_phrases',
] as const;

/**
 * Normalize a loaded resumejson in place: fill missing sections, coerce legacy
 * shapes, and guarantee every schema list field is an array.
 *
 * Two different rules, deliberately:
 *  - flat items + experience companies/roles are normalized IN PLACE
 *    (`ensureSchemaLists`), so nothing the record carries is ever dropped here;
 *  - experience bullets are REBUILT from the schema (`pickSchemaFields`),
 *    preserving the historical "drop unknown keys" behaviour at that one site —
 *    but now keeping every field the schema knows about, `include` included.
 */
export function normalizeResumeJson(loadedResume: any): any {
  // Ensure required structure exists with defaults
  if (!loadedResume.contact || !Array.isArray(loadedResume.contact)) {
    loadedResume.contact = [];
  }
  if (!loadedResume.experience) {
    loadedResume.experience = [];
  }
  if (!loadedResume.education) {
    loadedResume.education = [];
  }
  if (!loadedResume.skills) {
    loadedResume.skills = [];
  }
  if (!loadedResume.awards) {
    loadedResume.awards = [];
  }
  if (!loadedResume.presentations) {
    loadedResume.presentations = [];
  }
  if (!loadedResume.publications) {
    loadedResume.publications = [];
  }
  if (!loadedResume.portfolios) {
    loadedResume.portfolios = [];
  }
  if (!loadedResume.highlights) {
    loadedResume.highlights = [];
  }
  if (loadedResume.summary === undefined) {
    loadedResume.summary = '';
  }
  // summary_phrases is optional on older records (backward compat).
  if (!Array.isArray(loadedResume.summary_phrases)) loadedResume.summary_phrases = [];

  // Ensure skills are Skill objects
  if (loadedResume.skills.length > 0 && typeof loadedResume.skills[0] === 'string') {
    loadedResume.skills = (loadedResume.skills as string[]).map((s: string) => ({ title: s, tags: [] }));
  }
  // Alpha-sort skills on load only, so they're easy to find by name. New skills
  // added in-session append to the bottom (see addSkillToGroup); the next save+reload
  // re-runs this normalize pass and re-sorts them into place.
  loadedResume.skills.sort((a: any, b: any) =>
    (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
  );

  // Experience: company (url/roles) -> role (bullets) -> bullet.
  for (const company of loadedResume.experience) {
    if (!company || typeof company !== 'object') continue;
    ensureSchemaLists(company, 'experience');
    for (const role of company.roles) {
      if (!role || typeof role !== 'object') continue;
      ensureSchemaLists(role, 'experience.roles');
      role.bullets = role.bullets.map((b: any) => pickSchemaFields(b, 'experience.roles.bullets'));
    }
  }

  // Flat sections: guarantee every schema list field (tags, ...) is an array.
  for (const section of FLAT_SECTIONS) {
    for (const item of loadedResume[section]) {
      ensureSchemaLists(item, section);
    }
  }
  for (const item of loadedResume.contact) {
    ensureSchemaLists(item, 'contact');
  }

  // Ensure demographics object exists with defaults (backward compat — older
  // resumes predate this optional, extension-only section).
  loadedResume.demographics = { ...EMPTY_DEMOGRAPHICS, ...(loadedResume.demographics || {}) };
  // Implied facts: always an array so the card can iterate it. Malformed rows
  // are dropped here the same way the backend's normalize_facts_shape does.
  loadedResume.implied_facts = (Array.isArray(loadedResume.implied_facts)
    ? loadedResume.implied_facts
    : []
  ).filter((f: any) => f && typeof f === 'object' && typeof f.fact === 'string' && f.fact.trim())
    .map((f: any) => ({
      id: String(f.id || `if_${Math.random().toString(36).slice(2, 10)}`),
      fact: String(f.fact),
      status: f.status === 'confirmed' ? 'confirmed'
        : f.status === 'dismissed' ? 'dismissed'
        : 'proposed',
      source: f.source === 'user' ? 'user' : 'ai',
      created_at: String(f.created_at || ''),
    }));
  return loadedResume;
}

/**
 * Build the object that gets dumped to YAML for "Download / Export".
 *
 * Every item is projected through `exportSchemaFields`, so the file carries
 * exactly the schema-known fields the record actually has — including `include`
 * and the derived `_iso` dates — and the export -> hand-edit -> re-import round
 * trip preserves them (`resume/import_process.py` keeps the same schema fields
 * on the way back in).
 *
 * The top-level key for contacts stays the legacy plural `contacts`, which is
 * what the importer has always accepted and what every previously exported file
 * uses.
 */
export function buildYamlExportData(resume: ResumeJSON): Record<string, unknown> {
  const yamlData: Record<string, unknown> = {
    name: resume.name || 'Your Full Name',
  };

  if (resume.summary) {
    yamlData.summary = resume.summary;
  }

  if (resume.contact.length > 0) {
    yamlData.contacts = resume.contact.map(item => exportSchemaFields(item, 'contact'));
  }

  if (resume.skills.length > 0) {
    yamlData.skills = resume.skills.map(s => exportSchemaFields(s, 'skills'));
  }

  const experience = resume.experience || [];
  if (experience.length > 0) {
    yamlData.experience = experience.map(company => {
      const expEntry = exportSchemaFields(company, 'experience');
      // Nested item lists are re-projected item-by-item (exportSchemaFields
      // copies the raw child list; these overwrite it in place, keeping the
      // schema key order).
      expEntry.roles = (company.roles || []).map(role => {
        const roleEntry = exportSchemaFields(role, 'experience.roles');
        roleEntry.bullets = (role.bullets || []).map(
          bullet => exportSchemaFields(bullet, 'experience.roles.bullets')
        );
        return roleEntry;
      });
      return expEntry;
    });
  }

  if (resume.education.length > 0) {
    yamlData.education = resume.education.map(edu => exportSchemaFields(edu, 'education'));
  }

  if (resume.awards.length > 0) {
    yamlData.awards = resume.awards.map(award => exportSchemaFields(award, 'awards'));
  }

  if ((resume.presentations || []).length > 0) {
    yamlData.presentations = (resume.presentations || []).map(
      presentation => exportSchemaFields(presentation, 'presentations')
    );
  }

  if ((resume.publications || []).length > 0) {
    yamlData.publications = (resume.publications || []).map(
      pub => exportSchemaFields(pub, 'publications')
    );
  }

  if ((resume.portfolios || []).length > 0) {
    yamlData.portfolios = (resume.portfolios || []).map(
      port => exportSchemaFields(port, 'portfolios')
    );
  }

  return yamlData;
}
